import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(false);

  const visitorMapContainerRef = useRef(null);
  const visitorMapRef = useRef(null);

  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setIsAuthenticated(true);
      fetchVisitors(savedToken);
    }
  }, []);

  const fetchVisitors = async (token) => {
    setLoading(true);
    try {
      const response = await fetch('/api/visitors', {
        headers: { 'Authorization': token }
      });
      if (response.ok) {
        const data = await response.json();
        setVisitors(data);
      }
    } catch (err) {
      console.error('Visitors fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await response.json();
      if (data.success) {
        localStorage.setItem('admin_token', data.token);
        setIsAuthenticated(true);
        setErrorMsg('');
        fetchVisitors(data.token);
      } else {
        setErrorMsg('Hatalı Yönetici Şifresi!');
      }
    } catch (err) {
      setErrorMsg('Sunucu bağlantı hatası');
    }
  };

  // Map rendering
  useEffect(() => {
    if (!isAuthenticated || !visitorMapContainerRef.current || !visitors.length) return;

    if (visitorMapRef.current) {
      visitorMapRef.current.remove();
    }

    const map = L.map(visitorMapContainerRef.current, {
      center: [38.9637, 35.2433],
      zoom: 5,
      attributionControl: false
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png').addTo(map);

    visitors.forEach((visitor) => {
      if (!visitor.lat || !visitor.lon) return;

      const markerIcon = L.divIcon({
        className: '',
        html: `<div style="
          width: 14px;
          height: 14px;
          background: #ff3300;
          border: 2px solid #ffffff;
          border-radius: 50%;
          box-shadow: 0 0 6px rgba(0,0,0,0.4);
        "></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7]
      });

      const getDevice = (v) => {
        if (v.device) return v.device;
        const ua = v.userAgent || '';
        if (/iphone/i.test(ua)) return '📱 iPhone';
        if (/ipad/i.test(ua)) return '📱 iPad';
        if (/android/i.test(ua)) return '📱 Android';
        if (/windows/i.test(ua)) return '💻 Windows';
        if (/macintosh|mac os x/i.test(ua)) return '💻 Mac';
        if (/linux/i.test(ua)) return '💻 Linux';
        return '🖥️ Masaüstü';
      };

      const dateStr = new Date(visitor.timestamp).toLocaleString('tr-TR');
      const visitCountBadge = visitor.visitNumber ? `<span style="background:#000080;color:#fff;padding:1px 4px;border-radius:3px;font-size:10px;">${visitor.visitNumber}. Giriş</span>` : '';
      const popupContent = `
        <div style="font-family: sans-serif; font-size: 12px;">
          <b style="color: #000080;">${visitor.city}, ${visitor.country}</b> ${visitCountBadge}<br/>
          <b>Cihaz:</b> ${getDevice(visitor)}<br/>
          <b>ID:</b> <code style="font-size:10px;">${visitor.visitorId || 'Anonim'}</code><br/>
          <b>IP:</b> ${visitor.ip}<br/>
          <b>ISP:</b> ${visitor.isp}<br/>
          <b>Zaman:</b> ${dateStr}
        </div>
      `;

      L.marker([visitor.lat, visitor.lon], { icon: markerIcon })
        .addTo(map)
        .bindPopup(popupContent);
    });

    visitorMapRef.current = map;

    return () => {
      if (visitorMapRef.current) {
        visitorMapRef.current.remove();
        visitorMapRef.current = null;
      }
    };
  }, [isAuthenticated, visitors]);

  const getDeviceFromUA = (v) => {
    if (v.device) return v.device;
    const ua = v.userAgent || '';
    if (/iphone/i.test(ua)) return '📱 iPhone';
    if (/ipad/i.test(ua)) return '📱 iPad';
    if (/android/i.test(ua)) return '📱 Android';
    if (/windows/i.test(ua)) return '💻 Windows';
    if (/macintosh|mac os x/i.test(ua)) return '💻 Mac';
    if (/linux/i.test(ua)) return '💻 Linux';
    return '🖥️ Bilgisayar';
  };

  if (!isAuthenticated) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2 style={{ fontSize: '16px', color: '#000080', marginBottom: '12px' }}>🔒 Ziyaretçi Paneli (Admin)</h2>
        <form onSubmit={handleLogin} style={{ display: 'inline-block', textAlign: 'left', background: '#e0e0e0', padding: '16px', border: '2px solid #000' }}>
          <label style={{ display: 'block', fontSize: '12px', marginBottom: '6px' }}>Şifre Girin:</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ padding: '4px', fontSize: '14px', width: '100%', marginBottom: '10px' }}
            autoFocus
            required
          />
          {errorMsg && <div style={{ color: 'red', fontSize: '11px', marginBottom: '8px' }}>{errorMsg}</div>}
          <button type="submit" style={{ padding: '4px 12px', fontWeight: 'bold', cursor: 'pointer' }}>Giriş Yap</button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h2 style={{ fontSize: '14px', color: '#000080', margin: 0 }}>
          📍 Ziyaretçi Takip Paneli ({visitors.length} Kayıt)
        </h2>
        <button
          onClick={() => { const t = localStorage.getItem('admin_token'); fetchVisitors(t); }}
          style={{ padding: '3px 8px', fontSize: '11px', cursor: 'pointer' }}
        >
          🔄 Yenile
        </button>
      </div>

      {/* Map */}
      <div style={{ height: '220px', border: '2px solid #000', marginBottom: '12px' }}>
        <div ref={visitorMapContainerRef} style={{ width: '100%', height: '100%' }} />
      </div>

      {/* Visitor Logs Table */}
      <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #808080', background: '#fff' }}>
        {loading ? (
          <div style={{ padding: '10px', fontSize: '12px' }}>Yükleniyor...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ background: '#c0c0c0', borderBottom: '1px solid #000' }}>
                <th style={{ padding: '4px', textAlign: 'left' }}>Ziyaretçi ID</th>
                <th style={{ padding: '4px', textAlign: 'center' }}>Tekrar</th>
                <th style={{ padding: '4px', textAlign: 'left' }}>Cihaz</th>
                <th style={{ padding: '4px', textAlign: 'left' }}>IP</th>
                <th style={{ padding: '4px', textAlign: 'left' }}>Şehir / Ülke</th>
                <th style={{ padding: '4px', textAlign: 'left' }}>ISP</th>
                <th style={{ padding: '4px', textAlign: 'left' }}>Tarih</th>
              </tr>
            </thead>
            <tbody>
              {visitors.map((v) => (
                <tr key={v.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '4px', fontFamily: 'monospace', color: '#008000', fontWeight: 'bold' }}>
                    {v.visitorId ? v.visitorId.substring(0, 10) + '...' : 'Eski Kayıt'}
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                    {v.visitNumber ? `#${v.visitNumber}` : '1'}
                  </td>
                  <td style={{ padding: '4px', whiteSpace: 'nowrap' }}>
                    {getDeviceFromUA(v)}
                  </td>
                  <td style={{ padding: '4px', fontFamily: 'monospace' }}>{v.ip}</td>
                  <td style={{ padding: '4px' }}>{v.city} / {v.country}</td>
                  <td style={{ padding: '4px' }}>{v.isp}</td>
                  <td style={{ padding: '4px', color: '#000080' }}>
                    {new Date(v.timestamp).toLocaleString('tr-TR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Admin;
